import { MAX_ADDON_NESTING_DEPTH } from '@/api/menu-list/max-addon-nesting-depth';
import { nestSelections, type SelectionNode } from '@/entities/order/lib/group-selections';
import type { LocalizedString } from '@/shared/model/Language';

export type OrderReceiptLine = {
    name: LocalizedString;
    count: number;
    price: number;
    variantName?: LocalizedString;
    package?: boolean;
    children: OrderReceiptLine[];
};

export type MenuNameBody = {
    id: string;
    name?: LocalizedString | null;
    variants?: Array<{ id: string; itemId: string; isDefault?: boolean | null }> | null;
};

export type OrderSelectionRow = {
    id?: string | null;
    parentId?: string | null;
    itemId?: string | null;
    quantity?: string | number | null;
    price?: string | number | null;
    selectedVariantId?: string | null;
    item?: { name?: unknown } | null;
    packages?: Array<{
        price?: string | number | null;
        itemPackage?: { item?: { name?: unknown } | null } | null;
    }> | null;
};

const finiteQuantity = (value: string | number | null | undefined): number | undefined => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    if (value == null || value === '') return undefined;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const amountOf = (value: string | number | null | undefined): number => {
    const parsed = typeof value === 'number' ? value : parseFloat(value ?? '');
    return Number.isFinite(parsed) ? parsed : 0;
};

const asLocalized = (value: unknown): LocalizedString | undefined =>
    typeof value === 'string' || (value != null && typeof value === 'object')
        ? (value as LocalizedString)
        : undefined;

const sortLines = (lines: OrderReceiptLine[]): OrderReceiptLine[] => [
    ...lines.filter((line) => line.package),
    ...lines.filter((line) => !line.package && line.count >= 0),
    ...lines.filter((line) => !line.package && line.count < 0),
];

/** Nested addon and package lines for one order item, same tree as order history. */
export const selectionLines = (
    rows: readonly OrderSelectionRow[] | null | undefined,
    variantNameOf: (row: OrderSelectionRow) => LocalizedString | undefined,
): OrderReceiptLine[] => {
    const selections = (rows ?? []).filter(
        (row) => row.item?.name && finiteQuantity(row.quantity) !== undefined,
    );
    const toLines = (nodes: SelectionNode<OrderSelectionRow>[]): OrderReceiptLine[] => {
        const lines: OrderReceiptLine[] = [];
        for (const { row, children: nestedNodes } of nodes) {
            const quantity = finiteQuantity(row.quantity) ?? 0;
            const nested = toLines(nestedNodes);
            const ownPackages: OrderReceiptLine[] =
                quantity > 0
                    ? (row.packages ?? []).flatMap((pkg) => {
                          const name = asLocalized(pkg.itemPackage?.item?.name);
                          if (!name) return [];
                          return [
                              {
                                  name,
                                  count: 0,
                                  price: amountOf(pkg.price) * quantity,
                                  package: true,
                                  children: [],
                              },
                          ];
                      })
                    : [];
            if (quantity === 0 && nested.length === 0 && ownPackages.length === 0) continue;
            const name = asLocalized(row.item?.name);
            if (!name) continue;
            const variantName = variantNameOf(row);
            lines.push({
                name,
                count: quantity,
                price: quantity > 0 ? amountOf(row.price) * quantity : 0,
                ...(variantName ? { variantName } : {}),
                children: sortLines([...ownPackages, ...nested]),
            });
        }
        return sortLines(lines);
    };
    return toLines(nestSelections(selections, MAX_ADDON_NESTING_DEPTH));
};

/** Non-default variant name. `variantRowId` is the item_variant row, not the variant item id. */
export const variantNameFromMenu = (
    bodies: readonly MenuNameBody[] | null | undefined,
    itemId: string | null | undefined,
    variantRowId: string | null | undefined,
): LocalizedString | undefined => {
    if (!itemId || !variantRowId) return undefined;
    const item = (bodies ?? []).find((body) => body.id === itemId);
    const variant = item?.variants?.find((row) => row.id === variantRowId);
    if (!variant || variant.isDefault) return undefined;
    return (bodies ?? []).find((body) => body.id === variant.itemId)?.name ?? undefined;
};

/** Item name for an order variant stored as variantItemId. */
export const itemNameFromMenu = (
    bodies: readonly MenuNameBody[] | null | undefined,
    itemId: string | null | undefined,
): LocalizedString | undefined => {
    if (!itemId) return undefined;
    return (bodies ?? []).find((body) => body.id === itemId)?.name ?? undefined;
};
